import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { useTranslation } from "react-i18next";
import { normalizeRoleLevel } from "utils/roleUtils";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const FETCH_SIZE = 1000;
const SHOW_CALIBRATION_ACTIONS = true;

const tabList = [
  { key: "inventory", label: "機台庫存與操作" },
  { key: "missions", label: "檢視補貨任務" },
  { key: "logs", label: "機台庫存紀錄" },
  { key: "employees", label: "員工管理" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-TW", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getMachineStatusLabel = (machine) => {
  if (!machine?.is_online) return "離線";
  if (Number(machine?.current_qty || 0) === 0) return "空機";
  if (Number(machine?.current_qty || 0) <= 10) return "低庫存";
  return "上線";
};

const getStatusChipColor = (value) => {
  if (value === "上線") return "#2e7d32";
  if (value === "低庫存") return "#ef6c00";
  if (value === "空機") return "#c62828";
  if (value === "待處理") return "#ef6c00";
  if (value === "執行中") return "#1565c0";
  if (value === "已完成") return "#2e7d32";
  if (value === "已取消") return "#78909c";
  return "#78909c";
};

const getInventoryRowStatus = (row, qty) => {
  if (row?.status === "離線") return "離線";
  if (Number(qty || 0) === 0) return "空機";
  if (Number(qty || 0) <= 10) return "低庫存";
  return "上線";
};

const statusChip = (value) => (
  <Chip
    label={value}
    size="small"
    sx={{
      backgroundColor: getStatusChipColor(value),
      color: "#fff",
      fontWeight: 600,
      px: 1.2,
    }}
  />
);

const statsCardSx = (bg) => ({
  backgroundColor: bg,
  color: "#1b1b1b",
  borderRadius: "8px",
  px: 1.5,
  py: 0.75,
  width: "fit-content",
  minWidth: "auto",
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1rem",
  lineHeight: 1.4,
});

const tabButtonSx = {
  borderRadius: "12px",
  px: 4,
  py: 1,
  fontWeight: 600,
  minWidth: 160,
};

const primaryButtonSx = {
  ...tabButtonSx,
  backgroundColor: "#2f5be7",
  color: "#fff",
  "&:hover": { backgroundColor: "#2347c4" },
};

const outlineButtonSx = {
  ...tabButtonSx,
  border: "1px solid #2f5be7",
  color: "#2f5be7",
};

const actionButtonSx = {
  borderRadius: "10px",
  px: 3,
  py: 0.75,
  fontWeight: 600,
  minWidth: 112,
  borderWidth: "2px",
  borderColor: "#2f5be7",
  color: "#2f5be7",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#2347c4",
    backgroundColor: "rgba(47, 91, 231, 0.08)",
  },
  "&.Mui-disabled": {
    borderColor: "rgba(47, 91, 231, 0.55)",
    color: "rgba(47, 91, 231, 0.7)",
    backgroundColor: "rgba(47, 91, 231, 0.06)",
  },
};
const employeeActionButtonSx = {
  minWidth: 88,
  fontWeight: 700,
  borderWidth: "2px",
  color: "#1f5fd1",
  borderColor: "#2f6fe4",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#1a56c6",
    backgroundColor: "rgba(47, 111, 228, 0.08)",
  },
};
const employeeDisableButtonSx = {
  minWidth: 88,
  fontWeight: 700,
  borderWidth: "2px",
  color: "#1f5fd1",
  borderColor: "#2f6fe4",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#1a56c6",
    backgroundColor: "rgba(47, 111, 228, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(31, 95, 209, 0.38)",
    borderColor: "rgba(47, 111, 228, 0.22)",
    backgroundColor: "#ffffff",
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
  },
  "& .MuiSelect-select": {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
};

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const buildBackendBase = (graphqlEndpoint) =>
  trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));
const resolveBackendBase = (graphqlEndpoint) => {
  const base = buildBackendBase(graphqlEndpoint);
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};

const buildApiUrl = (base, path) => new URL(path, base).toString();

const normalizeEmployee = (employee) => ({
  id: employee.id ?? employee.ID ?? "",
  first_name: employee.first_name ?? employee.FirstName ?? "",
  last_name: employee.last_name ?? employee.LastName ?? "",
  code: employee.code ?? employee.Code ?? "",
  status: employee.status === "active" ? "active" : "inactive",
  merchant_id: employee.merchant_id ?? employee.MerchantID ?? "",
  token: employee.token ?? employee.Token ?? "",
  created_at: employee.created_at ?? employee.CreatedAt ?? "",
});

const buildStockOption = (stock) => {
  const sku = stock?.sku || stock?.barcode || stock?.ref || "";
  if (!sku) return null;
  const name = stock.display_name || stock.label || stock.name || sku;
  const qty =
    stock.qty !== null && stock.qty !== undefined && stock.qty !== "" ? Number(stock.qty) : null;
  return {
    label: qty === null || Number.isNaN(qty) ? `${name} (${sku})` : `${name} (${sku}) - ${qty}`,
    value: sku,
    productId: stock?.product_id ?? "",
    price:
      stock?.price !== null && stock?.price !== undefined && stock?.price !== ""
        ? Number(stock.price)
        : null,
  };
};

const firstFilled = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? "" : found;
};

const getMachineLookupKeys = (machine) =>
  [
    machine?.array_options?.options_device_id,
    machine?.device_id,
    machine?.machine_code,
    machine?.name,
    machine?.ref,
    machine?.label,
  ]
    .filter(Boolean)
    .map((key) => String(key));

const flattenWarehouseChildren = (children = []) =>
  children.reduce((acc, child) => {
    acc.push(child);
    const nestedChildren = Array.isArray(child?.children) ? child.children : [];
    if (nestedChildren.length) acc.push(...flattenWarehouseChildren(nestedChildren));
    return acc;
  }, []);

const pickStockMissionID = (payload) => {
  const data = payload?.data ?? payload;
  return firstFilled(
    typeof data === "string" ? data : "",
    typeof payload === "string" ? payload : "",
    data?.stock_mission_id,
    data?.mission_id,
    data?.id,
    payload?.stock_mission_id,
    payload?.mission_id,
    payload?.id
  );
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return Number.isFinite(Number(value)) ? Number(value) : value;
};

const statusText = (status) => {
  const labels = {
    pending: "待處理",
    in_progress: "執行中",
    completed: "已完成",
    cancelled: "已取消",
  };
  return labels[status] || status || "-";
};

const pickMissionList = (payload) => {
  if (Array.isArray(payload)) return payload;
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  return (
    data?.missions ||
    data?.mission_list ||
    data?.missionList ||
    data?.records ||
    data?.items ||
    data?.list ||
    []
  );
};

const isMissionInProgress = (mission) =>
  [mission.status, mission.from_status, mission.to_status].some(
    (status) => status === "in_progress"
  );

const isMissionCancellable = (mission) => {
  const statuses = [mission.status, mission.from_status, mission.to_status].filter(Boolean);
  if (!statuses.length) return true;
  if (statuses.some((status) => status === "completed")) return false;
  return statuses.some((status) => status !== "cancelled");
};

const normalizeMission = (mission) => {
  const missionID = firstFilled(mission.id, mission.mission_id, mission.stock_mission_id);
  const operation = firstFilled(
    mission.direction === "in" ? "入庫" : "",
    mission.direction === "out" ? "出庫" : "",
    mission.direction,
    mission.operation,
    mission.action,
    mission.mission_type === "restock" ? "補貨" : mission.mission_type
  );
  const currentQty = firstFilled(
    mission.current_qty,
    mission.currentQty,
    mission.from_actual_qty,
    mission.to_actual_qty
  );
  const needQty = firstFilled(
    mission.need_qty,
    mission.needQty,
    mission.qty_change,
    mission.to_expected_qty,
    mission.from_expected_qty
  );

  return {
    missionID,
    machineName: firstFilled(
      mission.device_id,
      mission.machine_id,
      mission.machineName,
      mission.machine_name,
      mission.to_warehouse_id,
      mission.from_warehouse_id,
      "-"
    ),
    operation: operation || "補貨",
    product: firstFilled(mission.product_name, mission.product, mission.name, mission.sku, "-"),
    currentQty: formatQuantity(currentQty),
    needQty: formatQuantity(needQty),
    employee: firstFilled(mission.executed_by, mission.employee, mission.created_by, "-"),
    createdAt: formatDateTime(mission.created_at),
    statusLabel: statusText(firstFilled(mission.status, mission.from_status, mission.to_status)),
    canCancel: isMissionCancellable(mission),
    allowInProgressCancel: isMissionInProgress(mission),
    raw: mission,
  };
};

const InventoryActionCell = ({ row, onOpen, onOpenCalibration }) => {
  const machine = row.original;
  const disabled = machine?.canOperate === false;

  return (
    <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <Button
        variant="outlined"
        size="small"
        sx={actionButtonSx}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onOpen(machine);
        }}
      >
        補貨任務
      </Button>
      {SHOW_CALIBRATION_ACTIONS ? (
        <Button
          variant="outlined"
          size="small"
          sx={actionButtonSx}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onOpenCalibration(machine);
          }}
        >
          校正
        </Button>
      ) : null}
    </MDBox>
  );
};

InventoryActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onOpenCalibration: PropTypes.func.isRequired,
};

const MissionCancelCell = ({ row, onOpen }) => (
  <Button
    variant="outlined"
    size="small"
    sx={actionButtonSx}
    disabled={!row.original?.canCancel}
    onClick={() => onOpen(row.original)}
  >
    取消
  </Button>
);

MissionCancelCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

const EmployeeActionCell = ({ row, onOpenEdit, onOpenDisable }) => {
  const isInactive = row.original?.status !== "active";
  return (
    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <Button
        size="small"
        variant="outlined"
        sx={employeeActionButtonSx}
        onClick={() => onOpenEdit(row.original)}
      >
        編輯
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        sx={employeeDisableButtonSx}
        disabled={isInactive}
        onClick={() => onOpenDisable(row.original)}
      >
        停用
      </Button>
    </Box>
  );
};

EmployeeActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onOpenEdit: PropTypes.func.isRequired,
  onOpenDisable: PropTypes.func.isRequired,
};

function WarehousesRealtime() {
  const { t } = useTranslation("common");
  const { userData, roleLevel, merchantId } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const backendBaseUrl = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);
  const currentMerchantId = useMemo(() => {
    if (!merchantId) return "";
    return String(merchantId)
      .split(",")
      .map((item) => item.trim())
      .find(Boolean);
  }, [merchantId]);
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const showMerchantFilter = normalizeRoleLevel(roleLevel) === 3;

  const fetchGraphQL = useCallback(
    async (query, variables = {}) => {
      const response = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ query, variables }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          json?.errors?.[0]?.message || `GraphQL request failed (${response.status})`
        );
      }
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }
      return json?.data || {};
    },
    [backendUrl]
  );

  const [activeTab, setActiveTab] = useState("inventory");
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [inventorySummary, setInventorySummary] = useState({
    total: 0,
    low_stock: 0,
    empty: 0,
  });
  const [inventoryRows, setInventoryRows] = useState([]);
  const [stockMissionRows, setStockMissionRows] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [warehouseData, setWarehouseData] = useState(null);
  const [missionWarehouseData, setMissionWarehouseData] = useState(null);
  const [calibrationWarehouseData, setCalibrationWarehouseData] = useState(null);
  const [missionWarehouseLoading, setMissionWarehouseLoading] = useState(false);
  const [calibrationWarehouseLoading, setCalibrationWarehouseLoading] = useState(false);

  const [missionOpen, setMissionOpen] = useState(false);
  const [missionTab, setMissionTab] = useState("in");
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [missionSubmitting, setMissionSubmitting] = useState(false);
  const [missionSuccess, setMissionSuccess] = useState({ open: false, message: "" });
  const [missionError, setMissionError] = useState({ open: false, message: "" });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [calibrationTarget, setCalibrationTarget] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [calibrationSubmitting, setCalibrationSubmitting] = useState(false);
  const [calibrationForm, setCalibrationForm] = useState({
    targetQty: "",
    reason: "",
  });
  const [missionForm, setMissionForm] = useState({
    batchNo: "",
    machineName: "",
    sku: "",
    ref: "",
    venueId: "",
    employeeCode: "",
    note: "",
    qty: "",
    fromWarehouseId: "",
    toWarehouseId: "",
  });

  const [merchantFilter, setMerchantFilter] = useState("");
  const [employeeCreateOpen, setEmployeeCreateOpen] = useState(false);
  const [employeeEditOpen, setEmployeeEditOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [disableEmployeeTarget, setDisableEmployeeTarget] = useState(null);
  const [disableEmployeeSubmitting, setDisableEmployeeSubmitting] = useState(false);
  const [employeeCreateForm, setEmployeeCreateForm] = useState({
    code: "",
    firstName: "",
    lastName: "",
  });
  const [employeeEditForm, setEmployeeEditForm] = useState({
    code: "",
    firstName: "",
    lastName: "",
    status: "",
    originStatus: "",
  });

  const currentQty = Number(selectedMachine?.currentQty || 0);
  const qtyChange = Math.max(Number(missionForm.qty || 0), 0);
  const targetQty =
    missionTab === "in" ? currentQty + qtyChange : Math.max(currentQty - qtyChange, 0);
  const needQty = qtyChange;
  const qtyFieldError =
    missionTab === "out" && qtyChange > currentQty ? "出庫數量不可大於目前數量" : "";
  const submitDisabled =
    missionSubmitting || missionWarehouseLoading || qtyChange <= 0 || Boolean(qtyFieldError);

  const employeeRows = useMemo(() => {
    const list = Array.isArray(employees) ? [...employees] : [];
    return list.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [employees]);

  const childWarehouseByDeviceId = useMemo(() => {
    const children = Array.isArray(warehouseData?.children) ? warehouseData.children : [];
    return flattenWarehouseChildren(children).reduce((acc, child) => {
      getMachineLookupKeys(child).forEach((key) => {
        acc[key] = child;
      });
      return acc;
    }, {});
  }, [warehouseData]);

  const calibrationChildWarehouse = useMemo(
    () => childWarehouseByDeviceId[calibrationTarget?.deviceId || ""] || null,
    [calibrationTarget, childWarehouseByDeviceId]
  );

  const calibrationWarehouseId = Number(
    firstFilled(
      calibrationTarget?.warehouseId,
      calibrationWarehouseData?.warehouse_id,
      calibrationChildWarehouse?.warehouse_id,
      0
    )
  );
  const calibrationWarehouseName = firstFilled(
    calibrationWarehouseData?.label,
    calibrationWarehouseData?.name,
    calibrationWarehouseData?.ref,
    calibrationChildWarehouse?.label,
    calibrationChildWarehouse?.name,
    calibrationChildWarehouse?.ref,
    calibrationTarget?.machineName,
    calibrationTarget?.deviceId,
    "-"
  );

  const missionChildWarehouseByDeviceId = useMemo(() => {
    const children = Array.isArray(missionWarehouseData?.children)
      ? missionWarehouseData.children
      : [];
    return flattenWarehouseChildren(children).reduce((acc, child) => {
      getMachineLookupKeys(child).forEach((key) => {
        acc[key] = child;
      });
      return acc;
    }, {});
  }, [missionWarehouseData]);

  const selectedMissionChildWarehouse = useMemo(
    () => missionChildWarehouseByDeviceId[selectedMachine?.deviceId || ""] || null,
    [missionChildWarehouseByDeviceId, selectedMachine]
  );

  const missionVenueLabel = useMemo(() => {
    const venueName = firstFilled(
      missionWarehouseData?.label,
      missionWarehouseData?.name,
      missionWarehouseData?.ref
    );
    const venueId = firstFilled(missionWarehouseData?.venue_id);
    if (venueName && venueId && String(venueName) !== String(venueId)) {
      return `${venueName} (${venueId})`;
    }
    return venueName || String(venueId || "");
  }, [missionWarehouseData]);

  const productOptions = useMemo(() => {
    const list =
      missionTab === "in"
        ? Array.isArray(missionWarehouseData?.stocks)
          ? missionWarehouseData.stocks
          : []
        : Array.isArray(selectedMissionChildWarehouse?.stocks)
        ? selectedMissionChildWarehouse.stocks
        : Array.isArray(selectedMachine?.stocks)
        ? selectedMachine.stocks
        : [];
    return list.map(buildStockOption).filter(Boolean);
  }, [missionTab, missionWarehouseData, selectedMachine, selectedMissionChildWarehouse]);

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(employeeRows) ? employeeRows : [];
    return list
      .filter((item) => item?.status === "active")
      .map((item) => ({
        label: `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.code,
        value: item.code || "",
      }));
  }, [employeeRows]);

  const selectedMissionProduct = useMemo(
    () => productOptions.find((option) => option.value === missionForm.sku) || null,
    [missionForm.sku, productOptions]
  );

  const resetMissionForm = useCallback(
    (machine, _nextTab = "in", nextMissionWarehouseData = null) => {
      const defaultVenueId = firstFilled(nextMissionWarehouseData?.venue_id);
      setMissionForm({
        batchNo: "",
        machineName: machine?.machineName && machine.machineName !== "-" ? machine.machineName : "",
        sku: "",
        ref: "",
        venueId: defaultVenueId,
        employeeCode: "",
        note: "",
        qty: "",
        fromWarehouseId: "",
        toWarehouseId: "",
      });
    },
    []
  );

  const fetchDashboard = useCallback(async () => {
    if (!email) return;
    const data = await fetchGraphQL(
      `
        query GetRestockDashboard($email: String!, $page: Int, $size: Int) {
          GetRestockDashboard(email: $email, page: $page, size: $size)
        }
      `,
      { email, page: 1, size: FETCH_SIZE }
    );
    const json = data?.GetRestockDashboard || {};
    const summary = json?.data?.summary || {};
    const machines = Array.isArray(json?.data?.machines) ? json.data.machines : [];

    setInventorySummary({
      total: Number(summary.total || 0),
      low_stock: Number(summary.low_stock || 0),
      empty: Number(summary.empty || 0),
    });

    setInventoryRows(
      machines.map((machine) => {
        const deviceId = machine.device_id || "";
        return {
          deviceId,
          warehouseId:
            machine.warehouse_id ||
            machine.machine_warehouse_id ||
            machine.child_warehouse_id ||
            machine.stock_warehouse_id ||
            "",
          stocks: Array.isArray(machine.stocks) ? machine.stocks : [],
          machineName: machine.machine_name || "-",
          venueName: machine.venue_name || "-",
          sku: machine.sku || "",
          product: machine.product_name || "-",
          currentQty: Number(machine.current_qty || 0),
          stock: Number(machine.current_qty || 0),
          status: getMachineStatusLabel(machine),
          lastShip: formatDateTime(machine.last_dispensed_at),
          canOperate: machine.can_operate,
        };
      })
    );
  }, [email, fetchGraphQL]);

  const fetchWarehouseData = useCallback(async () => {
    if (!email) return null;
    try {
      const data = await fetchGraphQL(
        `
          query GetWarehouseQueryV2($email: String!) {
            GetWarehouseQueryV2(email: $email)
          }
        `,
        { email }
      );
      const json = data?.GetWarehouseQueryV2 || {};
      const payload = json?.data || {};
      setWarehouseData(payload);
      return payload;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [email, fetchGraphQL]);

  const fetchMissionWarehouseData = useCallback(
    async (machine) => {
      const deviceId = machine?.deviceId;
      if (!deviceId || !backendBaseUrl || !mdmAuthKey) {
        setMissionWarehouseData(null);
        return null;
      }

      setMissionWarehouseLoading(true);
      try {
        const url = new URL(buildApiUrl(backendBaseUrl, "/api/v1/warehouse/queryv2/device"));
        url.searchParams.set("device_id", deviceId);

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Mdm-Auth": mdmAuthKey,
            Accept: "application/json",
          },
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(json?.message || `查詢機台倉庫失敗 (${response.status})`);
        }

        const payload = json?.data || {};
        setMissionWarehouseData(payload);
        setMissionForm((prev) => ({
          ...prev,
          venueId: String(firstFilled(payload?.venue_id)),
        }));
        return payload;
      } catch (error) {
        console.error(error);
        setMissionWarehouseData(null);
        setMissionError({
          open: true,
          message: error.message || "載入機台商品資料失敗",
        });
        return null;
      } finally {
        setMissionWarehouseLoading(false);
      }
    },
    [backendBaseUrl, mdmAuthKey]
  );

  const fetchRestockRecords = useCallback(async () => {
    if (!email) return;
    const data = await fetchGraphQL(
      `
        query GetRestockRecords($email: String!, $page: Int, $size: Int) {
          GetRestockRecords(email: $email, page: $page, size: $size)
        }
      `,
      { email, page: 1, size: FETCH_SIZE }
    );
    const json = data?.GetRestockRecords || {};
    const records = Array.isArray(json?.data?.records) ? json.data.records : [];
    setLogRows(
      records.map((record) => ({
        deviceId: record.device_id || "",
        machineName: record.machine_name || "-",
        direction: record.direction === "in" ? "入庫" : "出庫",
        referenceType: record.reference_type || "-",
        sku: record.sku || "",
        name: record.product_name || record.name || "-",
        beforeQty: Number(record.before_qty || 0),
        afterQty: Number(record.after_qty || 0),
        qtyChange: Number(record.qty_change || 0),
        source: record.source || "-",
        employee: record.employee || "-",
        createdAt: formatDateTime(record.created_at),
        reference: record.reference || "-",
      }))
    );
  }, [email, fetchGraphQL]);

  const fetchStockMissions = useCallback(async () => {
    if (!email) return;
    const data = await fetchGraphQL(
      `
        query GetRestockMissions($email: String!, $page: Int, $size: Int) {
          GetRestockMissions(email: $email, page: $page, size: $size)
        }
      `,
      { email, page: 1, size: FETCH_SIZE }
    );
    const json = data?.GetRestockMissions || {};
    const missions = pickMissionList(json);
    setStockMissionRows(Array.isArray(missions) ? missions.map(normalizeMission) : []);
  }, [email, fetchGraphQL]);

  const fetchEmployees = useCallback(async () => {
    if (!email) return [];
    try {
      const data = await fetchGraphQL(
        `
          query GetEmployeeList($email: String!, $merchantId: String) {
            GetEmployeeList(email: $email, merchantId: $merchantId)
          }
        `,
        {
          email,
          merchantId: showMerchantFilter ? merchantFilter || currentMerchantId : currentMerchantId,
        }
      );
      const json = data?.GetEmployeeList || {};
      const payload = Array.isArray(json.data) ? json.data.map(normalizeEmployee) : [];
      setEmployees(payload);
      return payload;
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [currentMerchantId, email, fetchGraphQL, merchantFilter, showMerchantFilter]);

  const refreshAll = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setPageError("");
    try {
      await Promise.all([
        fetchDashboard(),
        fetchStockMissions(),
        fetchRestockRecords(),
        fetchWarehouseData(),
        fetchEmployees(),
      ]);
    } catch (error) {
      console.error(error);
      setPageError(error.message || "載入失敗");
    } finally {
      setLoading(false);
    }
  }, [
    email,
    fetchDashboard,
    fetchEmployees,
    fetchRestockRecords,
    fetchStockMissions,
    fetchWarehouseData,
  ]);

  useEffect(() => {
    if (!email) return;
    refreshAll();
  }, [email, refreshAll]);

  useEffect(() => {
    if (!email) return;
    fetchEmployees();
    fetchWarehouseData();
  }, [email, fetchEmployees, fetchWarehouseData, merchantFilter]);

  useEffect(() => {
    if (!showMerchantFilter || merchantFilter || !currentMerchantId) return;
    setMerchantFilter(currentMerchantId);
  }, [currentMerchantId, merchantFilter, showMerchantFilter]);

  const handleOpenMission = useCallback(
    (machine) => {
      setSelectedMachine(machine);
      setMissionTab("in");
      resetMissionForm(machine, "in");
      setMissionWarehouseData(null);
      setMissionOpen(true);
      fetchMissionWarehouseData(machine);
    },
    [fetchMissionWarehouseData, resetMissionForm]
  );

  const handleOpenCalibration = useCallback((machine) => {
    setCalibrationTarget(machine);
    setCalibrationWarehouseData(null);
    setCalibrationForm({
      targetQty:
        machine?.currentQty !== null && machine?.currentQty !== undefined
          ? String(machine.currentQty)
          : "",
      reason: "",
    });
  }, []);

  const fetchCalibrationWarehouseData = useCallback(
    async (machine) => {
      const deviceId = machine?.deviceId;
      if (!deviceId || !backendBaseUrl || !mdmAuthKey) {
        setCalibrationWarehouseData(null);
        return null;
      }

      setCalibrationWarehouseLoading(true);
      try {
        const url = new URL(buildApiUrl(backendBaseUrl, "/api/v1/warehouse/queryv2/device"));
        url.searchParams.set("device_id", deviceId);

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Mdm-Auth": mdmAuthKey,
            Accept: "application/json",
          },
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(json?.message || `查詢機台倉庫失敗 (${response.status})`);
        }

        const payload = json?.data || {};
        setCalibrationWarehouseData(payload);
        return payload;
      } catch (error) {
        console.error(error);
        setCalibrationWarehouseData(null);
        setMissionError({
          open: true,
          message: error.message || "載入機台校正資料失敗",
        });
        return null;
      } finally {
        setCalibrationWarehouseLoading(false);
      }
    },
    [backendBaseUrl, mdmAuthKey]
  );

  useEffect(() => {
    if (!calibrationTarget) return;
    fetchCalibrationWarehouseData(calibrationTarget);
  }, [calibrationTarget, fetchCalibrationWarehouseData]);

  const handleChangeMissionTab = useCallback((nextTab) => {
    setMissionTab(nextTab);
    setMissionForm((prev) => ({
      ...prev,
      sku: "",
      ref: "",
      qty: "",
      fromWarehouseId: "",
      toWarehouseId: "",
    }));
  }, []);

  const handleCloseMission = useCallback(() => {
    setMissionOpen(false);
    setSelectedMachine(null);
    setMissionWarehouseData(null);
    resetMissionForm(null, "in");
  }, [resetMissionForm]);

  const handleSubmitMission = useCallback(async () => {
    const mainWarehouseId = Number(
      firstFilled(missionWarehouseData?.warehouse_id, warehouseData?.warehouse_id, 0)
    );
    const machineWarehouseId = Number(
      selectedMachine?.warehouseId || selectedMissionChildWarehouse?.warehouse_id || 0
    );
    const fromWarehouseId = missionTab === "in" ? mainWarehouseId : machineWarehouseId;
    const toWarehouseId = missionTab === "in" ? machineWarehouseId : mainWarehouseId;

    const missingFields = [];
    if (!email) missingFields.push("建立者信箱");
    if (!missionForm.batchNo) missingFields.push("任務批號/單號");
    if (!missionForm.sku) missingFields.push("商品 / SKU");
    if (!missionForm.employeeCode) missingFields.push("員工");
    if (missionTab === "in" && !missionForm.venueId) missingFields.push("場域");
    if (!mainWarehouseId) missingFields.push("主倉 warehouse_id");
    if (!machineWarehouseId) missingFields.push("機台 warehouse_id");
    if (!fromWarehouseId) missingFields.push("from_warehouse_id");
    if (!toWarehouseId) missingFields.push("to_warehouse_id");

    if (missingFields.length) {
      alert(`請填寫必填欄位：${missingFields.join("、")}`);
      return;
    }

    if (needQty <= 0) {
      alert("需補數量需大於 0");
      return;
    }

    if (qtyFieldError) {
      alert(qtyFieldError);
      return;
    }

    setMissionSubmitting(true);
    try {
      const missionData = await fetchGraphQL(
        `
          mutation CreateStockMissionV3($input: StockMissionCreateInput!) {
            CreateStockMissionV3(input: $input)
          }
        `,
        {
          input: {
            from_warehouse_id: fromWarehouseId,
            to_warehouse_id: toWarehouseId,
            sku: missionForm.sku,
            qty_change: Number(needQty),
            employee: missionForm.employeeCode,
            batch_no: missionForm.batchNo,
            note: missionForm.note,
            email,
          },
        }
      );

      if (missionTab === "in") {
        const stockMissionPayload = missionData?.CreateStockMissionV3 || {};
        const stockMissionID = pickStockMissionID(stockMissionPayload);
        const ref = String(missionForm.ref || "");
        const venueID = String(missionForm.venueId || "");
        const priceValue = selectedMissionProduct?.price;

        if (!stockMissionID) {
          throw new Error("建立補貨任務成功，但缺少 stock_mission_id");
        }
        // if (!ref) {
        //   throw new Error("建立入庫紀錄失敗：缺少外部貨號");
        // }
        if (!venueID) {
          throw new Error("建立入庫紀錄失敗：缺少場域 ID");
        }

        await fetchGraphQL(
          `
            mutation CreateStockMovement($input: StockMovementCreateInput!) {
              CreateStockMovement(input: $input)
            }
          `,
          {
            input: {
              ref,
              stock_mission_id: stockMissionID,
              email,
              venue_id: venueID,
              ...(Number.isFinite(priceValue) ? { price: Number(priceValue) } : {}),
            },
          }
        );
      }

      setMissionSuccess({ open: true, message: "補貨任務建立成功" });
      handleCloseMission();
      await Promise.all([fetchDashboard(), fetchStockMissions(), fetchRestockRecords()]);
    } catch (error) {
      console.error(error);
      setMissionError({ open: true, message: error.message || "補貨任務建立失敗" });
    } finally {
      setMissionSubmitting(false);
    }
  }, [
    email,
    fetchGraphQL,
    fetchDashboard,
    fetchStockMissions,
    fetchRestockRecords,
    handleCloseMission,
    missionForm,
    needQty,
    qtyFieldError,
    selectedMachine,
    selectedMissionProduct,
    selectedMissionChildWarehouse,
    t,
    missionWarehouseData,
    warehouseData,
    missionTab,
  ]);

  const handleCancelMission = useCallback(async () => {
    if (!cancelTarget?.missionID) return;
    setCancelSubmitting(true);
    try {
      await fetchGraphQL(
        `
          mutation CancelRestockMission($input: StockMissionCancelInput!) {
            CancelRestockMission(input: $input)
          }
        `,
        {
          input: {
            id: cancelTarget.missionID,
            operator: email,
            reason: "cancelled from backend admin",
            allow_in_progress: cancelTarget.allowInProgressCancel,
          },
        }
      );
      setMissionSuccess({ open: true, message: "補貨任務已取消" });
      setStockMissionRows((prevRows) =>
        prevRows.map((row) =>
          row.missionID === cancelTarget.missionID
            ? {
                ...row,
                statusLabel: "已取消",
                canCancel: false,
                allowInProgressCancel: false,
              }
            : row
        )
      );
      setCancelTarget(null);
      await Promise.all([fetchStockMissions(), fetchDashboard(), fetchRestockRecords()]);
    } catch (error) {
      console.error(error);
      setMissionError({ open: true, message: error.message || "取消補貨任務失敗" });
    } finally {
      setCancelSubmitting(false);
    }
  }, [cancelTarget, email, fetchDashboard, fetchGraphQL, fetchRestockRecords, fetchStockMissions]);

  const handleCloseCalibration = useCallback(() => {
    setCalibrationTarget(null);
    setCalibrationWarehouseData(null);
    setCalibrationForm({ targetQty: "", reason: "" });
  }, []);

  const applyCalibrationResult = useCallback((target, nextQty) => {
    if (!target?.deviceId) return;
    setInventoryRows((prevRows) =>
      prevRows.map((row) => {
        const isSameMachine = row.deviceId === target.deviceId;
        const isSameSku = !target.sku || !row.sku || row.sku === target.sku;
        if (!isSameMachine || !isSameSku) return row;

        return {
          ...row,
          currentQty: nextQty,
          stock: nextQty,
          status: getInventoryRowStatus(row, nextQty),
        };
      })
    );
  }, []);

  const handleSubmitCalibration = useCallback(async () => {
    const rawTargetQty = String(calibrationForm.targetQty).trim();
    const rawReason = String(calibrationForm.reason).trim();
    const targetQtyValue = Number(calibrationForm.targetQty);

    const missingFields = [];
    if (!calibrationWarehouseId) missingFields.push("倉庫名稱");
    if (!calibrationTarget?.sku) missingFields.push("商品名稱");
    if (rawTargetQty === "" || !Number.isInteger(targetQtyValue)) {
      missingFields.push("目標庫存數量");
    }
    if (!rawReason) missingFields.push("調整原因");

    if (missingFields.length) {
      alert(`請填寫必填欄位：${missingFields.join("、")}`);
      return;
    }

    if (targetQtyValue < 0) {
      alert("目標庫存數量不可小於 0");
      return;
    }

    setCalibrationSubmitting(true);
    try {
      const response = await fetch(buildApiUrl(backendBaseUrl, "/api/v1/stock/reconcile"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          warehouse_id: calibrationWarehouseId,
          sku: calibrationTarget.sku,
          target_qty: targetQtyValue,
          reason: rawReason,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || result?.error || `校正失敗 (${response.status})`);
      }

      setMissionSuccess({ open: true, message: "機台庫存校正成功" });
      const reconciledTarget = calibrationTarget;
      handleCloseCalibration();
      await Promise.all([fetchDashboard(), fetchRestockRecords()]);
      applyCalibrationResult(reconciledTarget, targetQtyValue);
    } catch (error) {
      console.error(error);
      setMissionError({ open: true, message: error.message || "機台庫存校正失敗" });
    } finally {
      setCalibrationSubmitting(false);
    }
  }, [
    backendBaseUrl,
    applyCalibrationResult,
    calibrationForm,
    calibrationTarget,
    calibrationWarehouseId,
    fetchDashboard,
    fetchRestockRecords,
    handleCloseCalibration,
    mdmAuthKey,
    t,
  ]);

  const handleOpenEmployeeEdit = useCallback((employee) => {
    setEditingEmployee(employee);
    setEmployeeEditForm({
      code: employee.code || "",
      firstName: employee.first_name || "",
      lastName: employee.last_name || "",
      status: employee.status === "active" ? "active" : "inactive",
      originStatus: employee.status === "active" ? "active" : "inactive",
    });
    setEmployeeEditOpen(true);
  }, []);

  const handleOpenEmployeeDisable = useCallback((employee) => {
    if (!employee?.code || employee.status !== "active") return;
    setDisableEmployeeTarget(employee);
  }, []);

  const handleCloseEmployeeDisable = useCallback(() => {
    if (disableEmployeeSubmitting) return;
    setDisableEmployeeTarget(null);
  }, [disableEmployeeSubmitting]);

  const handleCreateEmployee = useCallback(async () => {
    if (!employeeCreateForm.code || !employeeCreateForm.firstName || !employeeCreateForm.lastName) {
      alert(t("warehousesPage.messages.requiredFields"));
      return;
    }
    try {
      await fetchGraphQL(
        `
          mutation CreateEmployee($input: EmployeeCreateInput!) {
            CreateEmployee(input: $input)
          }
        `,
        {
          input: {
            code: employeeCreateForm.code,
            first_name: employeeCreateForm.firstName,
            last_name: employeeCreateForm.lastName,
            email,
            created_by_email: email,
          },
        }
      );
      alert(t("warehousesPage.messages.success"));
      setEmployeeCreateOpen(false);
      setEmployeeCreateForm({ code: "", firstName: "", lastName: "" });
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(t("warehousesPage.messages.createFailed"));
    }
  }, [email, employeeCreateForm, fetchEmployees, fetchGraphQL, t]);

  const handleUpdateEmployee = useCallback(async () => {
    if (!employeeEditForm.code) {
      alert(t("warehousesPage.messages.missingEmployeeCode"));
      return;
    }
    try {
      if (employeeEditForm.originStatus !== employeeEditForm.status) {
        const statusPath =
          employeeEditForm.status === "active"
            ? `/api/v1/employee/restore/${encodeURIComponent(employeeEditForm.code)}`
            : `/api/v1/employee/delete/${encodeURIComponent(employeeEditForm.code)}`;
        const statusResponse = await fetch(buildApiUrl(backendBaseUrl, statusPath), {
          method: employeeEditForm.status === "active" ? "PUT" : "DELETE",
          headers: { "Mdm-Auth": mdmAuthKey },
        });

        if (!statusResponse.ok) {
          throw new Error(`Request failed (${statusResponse.status})`);
        }
      }

      await fetchGraphQL(
        `
          mutation UpdateEmployee($input: EmployeeUpdateInput!) {
            UpdateEmployee(input: $input)
          }
        `,
        {
          input: {
            code: employeeEditForm.code,
            first_name: employeeEditForm.firstName,
            last_name: employeeEditForm.lastName,
            email,
          },
        }
      );
      alert(t("warehousesPage.messages.success"));
      setEmployeeEditOpen(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(t("warehousesPage.messages.updateFailed"));
    }
  }, [backendBaseUrl, email, employeeEditForm, fetchEmployees, fetchGraphQL, mdmAuthKey, t]);

  const handleDisableEmployee = useCallback(async () => {
    if (!disableEmployeeTarget?.code) return;
    setDisableEmployeeSubmitting(true);
    try {
      const statusPath = `/api/v1/employee/delete/${encodeURIComponent(
        disableEmployeeTarget.code
      )}`;
      const statusResponse = await fetch(buildApiUrl(backendBaseUrl, statusPath), {
        method: "DELETE",
        headers: { "Mdm-Auth": mdmAuthKey },
      });

      if (!statusResponse.ok) {
        throw new Error(`Request failed (${statusResponse.status})`);
      }

      alert("已停用");
      setDisableEmployeeTarget(null);
      if (editingEmployee?.code === disableEmployeeTarget.code) {
        setEmployeeEditOpen(false);
        setEditingEmployee(null);
      }
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert("停用失敗");
    } finally {
      setDisableEmployeeSubmitting(false);
    }
  }, [backendBaseUrl, disableEmployeeTarget, editingEmployee, fetchEmployees, mdmAuthKey]);

  const InventoryActionCellRenderer = ({ row }) => (
    <InventoryActionCell
      row={row}
      onOpen={handleOpenMission}
      onOpenCalibration={handleOpenCalibration}
    />
  );
  InventoryActionCellRenderer.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  const MissionCancelCellRenderer = ({ row }) => (
    <MissionCancelCell row={row} onOpen={setCancelTarget} />
  );
  MissionCancelCellRenderer.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  const EmployeeEditCellRenderer = ({ row }) => (
    <EmployeeActionCell
      row={row}
      onOpenEdit={handleOpenEmployeeEdit}
      onOpenDisable={handleOpenEmployeeDisable}
    />
  );
  EmployeeEditCellRenderer.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  const inventoryColumns = useMemo(
    () => [
      { Header: "機台 ID", accessor: "deviceId" },
      { Header: "商品", accessor: "product" },
      { Header: "場域", accessor: "venueName" },
      { Header: "即時庫存", accessor: "stock" },
      {
        Header: "狀態",
        accessor: "status",
        Cell: ({ value }) => statusChip(value),
      },
      { Header: "最後出蛋時間", accessor: "lastShip" },
      {
        Header: "操作",
        accessor: "actions",
        Cell: InventoryActionCellRenderer,
      },
    ],
    [InventoryActionCellRenderer]
  );

  const missionColumns = useMemo(
    () => [
      { Header: "機台名稱", accessor: "machineName" },
      { Header: "操作", accessor: "operation" },
      { Header: "商品", accessor: "product" },
      { Header: "目前數量", accessor: "currentQty" },
      { Header: "需補數量", accessor: "needQty" },
      { Header: "員工", accessor: "employee" },
      { Header: "時間", accessor: "createdAt" },
      {
        Header: "任務取消",
        accessor: "cancel",
        Cell: MissionCancelCellRenderer,
      },
      {
        Header: "狀態",
        accessor: "statusLabel",
        Cell: ({ value }) => statusChip(value),
      },
    ],
    [MissionCancelCellRenderer]
  );

  const logColumns = useMemo(
    () => [
      { Header: "機台 ID", accessor: "deviceId" },
      { Header: "品名", accessor: "name" },
      { Header: "SKU", accessor: "sku" },
      { Header: "操作前數量", accessor: "beforeQty" },
      { Header: "操作後數量", accessor: "afterQty" },
      { Header: "異動數量", accessor: "qtyChange" },
      { Header: "建立時間", accessor: "createdAt" },
    ],
    []
  );

  const employeeColumns = useMemo(
    () => [
      {
        Header: "操作",
        accessor: "actions",
        Cell: EmployeeEditCellRenderer,
      },
      {
        Header: "狀態",
        accessor: "status",
        Cell: ({ value }) => (value === "active" ? "啟用" : "停用"),
      },
      { Header: "ID", accessor: "id" },
      { Header: "名字", accessor: "first_name" },
      { Header: "姓氏", accessor: "last_name" },
      { Header: "員工代碼", accessor: "code" },
      { Header: "商家", accessor: "merchant_id" },
      { Header: "Token", accessor: "token" },
      { Header: "建立時間", accessor: "created_at" },
    ],
    [EmployeeEditCellRenderer]
  );

  const renderMissionSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title="成功"
      content={missionSuccess.message}
      dateTime="剛剛"
      open={missionSuccess.open}
      onClose={() => setMissionSuccess({ open: false, message: "" })}
      close={() => setMissionSuccess({ open: false, message: "" })}
      bgWhite
    />
  );

  const renderMissionErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="錯誤"
      content={missionError.message}
      dateTime="剛剛"
      open={missionError.open}
      onClose={() => setMissionError({ open: false, message: "" })}
      close={() => setMissionError({ open: false, message: "" })}
      bgWhite
    />
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <MDTypography variant="h6" color="white">
                    <RoutePageTitle />
                  </MDTypography>
                  <Button
                    variant="contained"
                    sx={{
                      ...outlineButtonSx,
                      color: "#fff",
                      borderColor: "#fff",
                      width: { xs: "100%", sm: "auto" },
                    }}
                    onClick={refreshAll}
                    disabled={loading}
                  >
                    重新整理
                  </Button>
                </MDBox>
              </MDBox>

              <MDBox px={3} py={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.5, sm: 2 },
                    width: "100%",
                  }}
                >
                  {tabList.map((tab) => (
                    <Button
                      key={tab.key}
                      variant={activeTab === tab.key ? "contained" : "outlined"}
                      sx={{
                        ...(activeTab === tab.key ? primaryButtonSx : outlineButtonSx),
                        width: "100%",
                        minHeight: 44,
                        px: { xs: 1, sm: 2 },
                        whiteSpace: "normal",
                        lineHeight: 1.25,
                        boxSizing: "border-box",
                      }}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </Box>
              </MDBox>

              <MDBox px={3} pb={3}>
                {activeTab === "inventory" && (
                  <MDBox display="flex" gap={1.25} flexWrap="wrap" mb={3} alignItems="center">
                    <MDBox sx={statsCardSx("#c6c6c6")}>機台總數：{inventorySummary.total}</MDBox>
                    <MDBox sx={statsCardSx("#ffb4b0")}>低庫存：{inventorySummary.low_stock}</MDBox>
                    <MDBox sx={statsCardSx("#ffe082")}>空機：{inventorySummary.empty}</MDBox>
                  </MDBox>
                )}

                {loading && (
                  <MDBox display="flex" justifyContent="flex-end" mb={3}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      <MDTypography variant="button">載入中</MDTypography>
                    </MDBox>
                  </MDBox>
                )}

                {pageError && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" color="error">
                      {pageError}
                    </MDTypography>
                  </MDBox>
                )}

                {activeTab === "inventory" && (
                  <DataTable
                    table={{ columns: inventoryColumns, rows: inventoryRows }}
                    tabValue={activeTab}
                    entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                    canSearch
                    showTotalEntries={false}
                    isSorted
                    noEndBorder
                  />
                )}

                {activeTab === "missions" && (
                  <DataTable
                    table={{ columns: missionColumns, rows: stockMissionRows }}
                    tabValue={activeTab}
                    entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                    canSearch
                    showTotalEntries={false}
                    isSorted
                    noEndBorder
                  />
                )}

                {activeTab === "logs" && (
                  <DataTable
                    table={{ columns: logColumns, rows: logRows }}
                    tabValue={activeTab}
                    entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                    canSearch
                    showTotalEntries={false}
                    isSorted
                    noEndBorder
                  />
                )}

                {activeTab === "employees" && (
                  <MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
                      <MDTypography variant="h6">
                        {t("warehousesPage.sections.employees")}
                      </MDTypography>
                      {showMerchantFilter && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel>{t("warehousesPage.form.merchant")}</InputLabel>
                          <Select
                            value={merchantFilter}
                            label={t("warehousesPage.form.merchant")}
                            onChange={(event) => setMerchantFilter(event.target.value)}
                            sx={selectSx}
                          >
                            <MenuItem value="BK">BK</MenuItem>
                            <MenuItem value="CM">CM</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      <Button
                        variant="outlined"
                        sx={outlineButtonSx}
                        onClick={() => setEmployeeCreateOpen(true)}
                      >
                        {t("warehousesPage.actions.createEmployee")}
                      </Button>
                    </MDBox>
                    <DataTable
                      table={{ columns: employeeColumns, rows: employeeRows }}
                      tabValue={activeTab}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={missionOpen} onClose={handleCloseMission} fullWidth maxWidth="md">
        <DialogTitle>{`機台補貨任務-${missionTab === "in" ? "入庫" : "出庫"}`}</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" gap={2} mb={2}>
            <Button
              variant={missionTab === "in" ? "contained" : "outlined"}
              sx={missionTab === "in" ? primaryButtonSx : outlineButtonSx}
              onClick={() => handleChangeMissionTab("in")}
            >
              入庫
            </Button>
            <Button
              variant={missionTab === "out" ? "contained" : "outlined"}
              sx={missionTab === "out" ? primaryButtonSx : outlineButtonSx}
              onClick={() => handleChangeMissionTab("out")}
            >
              出庫
            </Button>
          </MDBox>
          <Divider />
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="任務批號/單號"
                fullWidth
                size="small"
                value={missionForm.batchNo}
                onChange={(event) =>
                  setMissionForm((prev) => ({ ...prev, batchNo: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="目前數量"
                fullWidth
                size="small"
                value={currentQty}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="機台 ID"
                fullWidth
                size="small"
                value={selectedMachine?.deviceId || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            {missionTab === "in" && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="場域"
                  fullWidth
                  size="small"
                  value={missionVenueLabel}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={productOptions}
                loading={missionWarehouseLoading}
                value={selectedMissionProduct}
                onChange={(_, option) =>
                  setMissionForm((prev) => ({ ...prev, sku: option?.value || "" }))
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ""}
                noOptionsText={missionWarehouseLoading ? "載入中" : "查無相關商品"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="商品 / SKU"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {missionWarehouseLoading ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={missionTab === "in" ? "入庫數量" : "出庫數量"}
                fullWidth
                size="small"
                type="number"
                value={missionForm.qty}
                error={Boolean(qtyFieldError)}
                helperText={qtyFieldError || " "}
                onChange={(event) =>
                  setMissionForm((prev) => ({ ...prev, qty: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>員工</InputLabel>
                <Select
                  value={missionForm.employeeCode}
                  label="員工"
                  onChange={(event) =>
                    setMissionForm((prev) => ({ ...prev, employeeCode: event.target.value }))
                  }
                >
                  {employeeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="建立者信箱"
                fullWidth
                size="small"
                value={email}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="外部貨號"
                fullWidth
                size="small"
                value={missionForm.ref}
                onChange={(event) =>
                  setMissionForm((prev) => ({ ...prev, ref: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="原因 / 備註"
                fullWidth
                size="small"
                value={missionForm.note}
                onChange={(event) =>
                  setMissionForm((prev) => ({ ...prev, note: event.target.value }))
                }
              />
            </Grid>
          </Grid>
          <MDBox mt={3} display="flex" flexWrap="wrap" gap={3}>
            <MDTypography variant="button" fontWeight="medium">
              {`目標數量：${targetQty}`}
            </MDTypography>
            <MDTypography variant="button" fontWeight="medium">
              {`需補數量：${needQty}`}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={outlineButtonSx} onClick={handleCloseMission}>
            關閉
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={handleSubmitMission}
            disabled={submitDisabled}
          >
            {missionSubmitting ? "送出中" : "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(calibrationTarget)}
        onClose={handleCloseCalibration}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>機台校正</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="倉庫名稱"
                fullWidth
                size="small"
                value={calibrationWarehouseName}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="商品名稱"
                fullWidth
                size="small"
                value={calibrationTarget?.product || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="目標庫存數量"
                fullWidth
                size="small"
                type="number"
                value={calibrationForm.targetQty}
                onChange={(event) =>
                  setCalibrationForm((prev) => ({ ...prev, targetQty: event.target.value }))
                }
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="調整原因"
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={calibrationForm.reason}
                onChange={(event) =>
                  setCalibrationForm((prev) => ({ ...prev, reason: event.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleCloseCalibration}
            disabled={calibrationSubmitting || calibrationWarehouseLoading}
          >
            關閉
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={handleSubmitCalibration}
            disabled={calibrationSubmitting || calibrationWarehouseLoading}
          >
            {calibrationSubmitting || calibrationWarehouseLoading ? "送出中" : "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>是否取消此補貨任務？</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={1}>
            <MDTypography variant="button">任務 ID：{cancelTarget?.missionID || "-"}</MDTypography>
            <MDTypography variant="button">
              機台名稱：{cancelTarget?.machineName || "-"}
            </MDTypography>
            <MDTypography variant="button">
              取消方式：{cancelTarget?.allowInProgressCancel ? "中途取消" : "安全取消"}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setCancelTarget(null)}
            disabled={cancelSubmitting}
          >
            否
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={handleCancelMission}
            disabled={cancelSubmitting}
          >
            {cancelSubmitting ? "取消中" : "是"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={employeeCreateOpen}
        onClose={() => setEmployeeCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("warehousesPage.dialogs.employeeCreate")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.code")}
                fullWidth
                size="small"
                value={employeeCreateForm.code}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, code: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.firstName")}
                fullWidth
                size="small"
                value={employeeCreateForm.firstName}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.lastName")}
                fullWidth
                size="small"
                value={employeeCreateForm.lastName}
                onChange={(event) =>
                  setEmployeeCreateForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setEmployeeCreateOpen(false)}
          >
            {t("warehousesPage.actions.close")}
          </Button>
          <Button variant="contained" sx={primaryButtonSx} onClick={handleCreateEmployee}>
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={employeeEditOpen}
        onClose={() => setEmployeeEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("warehousesPage.dialogs.employeeEdit")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.code")}
                fullWidth
                size="small"
                value={employeeEditForm.code}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.firstName")}
                fullWidth
                size="small"
                value={employeeEditForm.firstName}
                onChange={(event) =>
                  setEmployeeEditForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("warehousesPage.form.lastName")}
                fullWidth
                size="small"
                value={employeeEditForm.lastName}
                onChange={(event) =>
                  setEmployeeEditForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <MDTypography variant="button" fontWeight="regular">
                  {`目前狀態：${employeeEditForm.status === "active" ? "啟用" : "停用"}`}
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={() => setEmployeeEditOpen(false)}
          >
            {t("warehousesPage.actions.close")}
          </Button>
          <Button variant="contained" sx={primaryButtonSx} onClick={handleUpdateEmployee}>
            {t("warehousesPage.actions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(disableEmployeeTarget)}
        onClose={handleCloseEmployeeDisable}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>確認停用</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" fontWeight="regular">
            是否確定停用員工 {disableEmployeeTarget?.code || "-"}？
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={outlineButtonSx}
            onClick={handleCloseEmployeeDisable}
            disabled={disableEmployeeSubmitting}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisableEmployee}
            disabled={disableEmployeeSubmitting}
          >
            {disableEmployeeSubmitting ? "停用中..." : "確認停用"}
          </Button>
        </DialogActions>
      </Dialog>

      {renderMissionSuccessSB}
      {renderMissionErrorSB}
    </DashboardLayout>
  );
}

export default WarehousesRealtime;
